import { useMemo, useState } from 'react';
import { X, Monitor, Tablet, Smartphone } from 'lucide-react';
import { Template } from '@/lib/templates';
import { renderTemplate, buildTemplatePreviewData, getAllTemplateMetadata } from '@shared/siteRenderer';

const metadataMap = new Map(getAllTemplateMetadata().map(m => [m.id, m]));

interface TemplateFullPreviewProps {
  template: Template;
  businessName?: string;
  onClose: () => void;
  selectedFeatures: string[];
  onSelectedFeaturesChange?: (features: string[]) => void;
}

type Breakpoint = 'desktop' | 'tablet' | 'mobile';

export function TemplateFullPreview({
  template,
  onClose,
  selectedFeatures,
}: TemplateFullPreviewProps) {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('desktop');

  const width = breakpoint === 'mobile' ? '390px' : breakpoint === 'tablet' ? '820px' : '100%';
  const frameMaxWidth = breakpoint === 'desktop' ? '1280px' : width;

  const previewHtml = useMemo(() => {
    const placeholderData = buildTemplatePreviewData(template.id);
    const meta = metadataMap.get(template.id);
    const features = selectedFeatures.length > 0 ? selectedFeatures : (meta?.defaultEnabledFeatures ?? []);
    let html = renderTemplate(template.id, placeholderData, features);
    // Intercept all link clicks: hash links scroll in-place, others are blocked
    const linkScript = `<script>
document.addEventListener('click', function(e) {
  var link = e.target.closest('a');
  if (!link) return;
  var href = link.getAttribute('href');
  if (!href) return;
  if (href.startsWith('#')) {
    e.preventDefault();
    if (href === '#') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    var target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
    return;
  }
  e.preventDefault();
});
<\/script>`;
    html = html.replace('</body>', linkScript + '</body>');
    return html;
  }, [template, selectedFeatures]);

  return (
    <div className="animate-modal-backdrop" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(91,58,58,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFBFE' }}>{template.name}</h2>
          <p style={{ fontSize: '13px', color: '#888' }}>Template preview with sample data &mdash; toggle features in the wizard to update</p>
        </div>
        <button onClick={onClose} style={{ padding: '8px', borderRadius: '8px', background: 'rgba(91,58,58,0.1)', border: '1px solid rgba(91,58,58,0.2)', color: '#B08080', cursor: 'pointer' }}>
          <X style={{ width: '20px', height: '20px' }} />
        </button>
      </div>

      <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(91,58,58,0.15)', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {[{ key: 'mobile', icon: Smartphone }, { key: 'tablet', icon: Tablet }, { key: 'desktop', icon: Monitor }].map(({ key, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setBreakpoint(key as Breakpoint)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(91,58,58,0.2)', background: breakpoint === key ? 'rgba(176,128,128,0.3)' : 'rgba(91,58,58,0.1)', color: breakpoint === key ? '#B08080' : '#888', cursor: 'pointer' }}
          >
            <Icon style={{ width: '16px', height: '16px' }} />
          </button>
        ))}
        <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
          {selectedFeatures.length} features enabled
        </span>
      </div>

      <div style={{ flex: 1, padding: '24px', overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: frameMaxWidth, border: breakpoint === 'desktop' ? 'none' : '10px solid #222', borderRadius: breakpoint === 'desktop' ? '0' : '20px', overflow: 'hidden' }}>
          <iframe
            title="Template Full Preview"
            srcDoc={previewHtml}
            style={{ width: '100%', height: 'calc(100vh - 200px)', border: 0, background: '#fff' }}
            sandbox="allow-scripts"
          />
        </div>
      </div>
    </div>
  );
}
